import { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import DemoQRCode from "@/components/DemoQRCode";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { Printer, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";

/**
 * Printable business card — 3.5" × 2" US standard, navy + gold Decline Drive
 * brand. Front + back. Editable contact fields persisted to localStorage so
 * Justin can update his own name/title/phone without touching code.
 *
 * Print: uses @page rule scoped to this component's <style jsx global> to force
 * a card-sized page. When printing, everything except .card-print-area is
 * hidden (no-print helper class controls this).
 */

const STORAGE_KEY = "declinedrive_business_card_v1";
const DEFAULTS = {
  name: "James Ayers",
  title: "Founder & CEO",
  phone: "(555) 555-0100",
  email: "james@declinedrive.com",
  website: "declinedrive.com",
  tagline: "We Drive Further",
  photo: "", // data-URL base64 headshot — printed on the back of the card
};

export default function BusinessCardPage() {
  const [fields, setFields] = useState(DEFAULTS);
  const [logoUrl, setLogoUrl] = useState("");

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setFields({ ...DEFAULTS, ...JSON.parse(raw) });
    } catch {
      /* ignore */
    }
    api
      .get("/settings")
      .then((r) => setLogoUrl(r.data?.dealership_logo_url || ""))
      .catch(() => {});
  }, []);

  const save = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(fields));
    toast.success("Card details saved — print-ready");
  };
  const reset = () => {
    setFields(DEFAULTS);
    localStorage.removeItem(STORAGE_KEY);
    toast.success("Reset to defaults");
  };
  const set = (k, v) => setFields((f) => ({ ...f, [k]: v }));

  return (
    <DashboardShell>
      {/* --- Print stylesheet: card-sized pages only when printing --- */}
      <style>{`
        @media print {
          @page { size: 3.5in 2in; margin: 0; }
          body { background: #fff !important; }
          .no-print { display: none !important; }
          .card-print-area { display: block !important; }
          .biz-card {
            page-break-after: always;
            break-after: page;
            margin: 0 !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            width: 3.5in !important;
            height: 2in !important;
          }
          .biz-card:last-child { page-break-after: auto; }
        }
      `}</style>

      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap no-print">
        <div>
          <div className="font-mono-label text-muted-foreground">Print Kit</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">
            Business Card
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl leading-relaxed">
            US standard 3.5&quot; × 2&quot; · Front + Back. Fill in your details on the
            left, live preview updates on the right. Print at 100% scale to
            standard card stock (Moo, VistaPrint, or your local printer). QR
            back sends the taker straight to <code>/demo</code>.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={reset} data-testid="biz-card-reset-btn">
            <RotateCcw className="w-4 h-4 mr-1.5" strokeWidth={2} />
            Reset
          </Button>
          <Button variant="outline" onClick={save} data-testid="biz-card-save-btn">
            <Save className="w-4 h-4 mr-1.5" strokeWidth={2} />
            Save details
          </Button>
          <Button
            onClick={() => window.print()}
            className="bg-navy hover:bg-navy/90 text-white"
            data-testid="biz-card-print-btn"
          >
            <Printer className="w-4 h-4 mr-1.5" strokeWidth={2} />
            Print card
          </Button>
        </div>
      </div>

      {/* --- Editor + Preview split --- */}
      <div className="grid lg:grid-cols-[1fr_1.4fr] gap-6">
        <Card className="p-5 border-border no-print" data-testid="biz-card-editor">
          <div className="font-mono-label text-muted-foreground text-[0.6rem] mb-3">
            Your details
          </div>
          <div className="space-y-3">
            <div>
              <Label htmlFor="bc-name" className="font-mono-label text-[0.6rem]">
                Full name
              </Label>
              <Input
                id="bc-name"
                value={fields.name}
                onChange={(e) => set("name", e.target.value)}
                className="mt-1"
                data-testid="biz-card-name-input"
              />
            </div>
            <div>
              <Label htmlFor="bc-title" className="font-mono-label text-[0.6rem]">
                Title
              </Label>
              <Input
                id="bc-title"
                value={fields.title}
                onChange={(e) => set("title", e.target.value)}
                className="mt-1"
                data-testid="biz-card-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="bc-phone" className="font-mono-label text-[0.6rem]">
                  Phone
                </Label>
                <Input
                  id="bc-phone"
                  value={fields.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  className="mt-1"
                  data-testid="biz-card-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="bc-email" className="font-mono-label text-[0.6rem]">
                  Email
                </Label>
                <Input
                  id="bc-email"
                  value={fields.email}
                  onChange={(e) => set("email", e.target.value)}
                  className="mt-1"
                  data-testid="biz-card-email-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="bc-web" className="font-mono-label text-[0.6rem]">
                Website
              </Label>
              <Input
                id="bc-web"
                value={fields.website}
                onChange={(e) => set("website", e.target.value)}
                className="mt-1"
                data-testid="biz-card-web-input"
              />
            </div>
            <div>
              <Label htmlFor="bc-tag" className="font-mono-label text-[0.6rem]">
                Tagline
              </Label>
              <Input
                id="bc-tag"
                value={fields.tagline}
                onChange={(e) => set("tagline", e.target.value)}
                className="mt-1"
                data-testid="biz-card-tag-input"
              />
            </div>
            <div>
              <Label htmlFor="bc-photo" className="font-mono-label text-[0.6rem]">
                Headshot (optional · shown on card back)
              </Label>
              <div className="flex items-center gap-3 mt-1.5">
                {fields.photo ? (
                  <img
                    src={fields.photo}
                    alt="Preview"
                    className="w-14 h-14 rounded-full object-cover border-2 border-border"
                    data-testid="biz-card-photo-preview"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full border-2 border-dashed border-border flex items-center justify-center text-[0.55rem] font-mono-label text-muted-foreground">
                    NO PHOTO
                  </div>
                )}
                <div className="flex-1 flex gap-2">
                  <input
                    id="bc-photo"
                    type="file"
                    accept="image/*"
                    className="text-xs"
                    data-testid="biz-card-photo-input"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      if (f.size > 2 * 1024 * 1024) {
                        toast.error("Photo too large — keep it under 2 MB");
                        return;
                      }
                      const reader = new FileReader();
                      reader.onload = () => set("photo", String(reader.result || ""));
                      reader.onerror = () => toast.error("Couldn't read that file");
                      reader.readAsDataURL(f);
                    }}
                  />
                  {fields.photo && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => set("photo", "")}
                      data-testid="biz-card-photo-remove"
                      className="text-xs h-8"
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>
              <div className="text-[0.6rem] text-muted-foreground mt-1.5 leading-snug">
                Under 2 MB · square crop looks best. Stays on your device — never uploaded.
              </div>
            </div>
          </div>
        </Card>

        {/* Preview stack — this same block prints */}
        <div className="card-print-area space-y-6 flex flex-col items-center">
          <BusinessCardFront fields={fields} logoUrl={logoUrl} />
          <BusinessCardBack fields={fields} logoUrl={logoUrl} />
        </div>
      </div>
    </DashboardShell>
  );
}

/* ---------- Card faces ---------- */

function BusinessCardFront({ fields, logoUrl }) {
  return (
    <div
      className="biz-card relative bg-[#0f2b6b] text-white overflow-hidden shadow-xl rounded-md"
      style={{ width: "3.5in", height: "2in" }}
      data-testid="biz-card-front"
    >
      {/* Gold accent stripe left */}
      <div
        className="absolute left-0 top-0 bottom-0 w-[0.15in] bg-[#d4a640]"
        aria-hidden
      />
      {/* Subtle diagonal grain */}
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(135deg, #fff 0 1px, transparent 1px 8px)",
        }}
        aria-hidden
      />

      {/* Logo mark top-right */}
      <div className="absolute top-[0.18in] right-[0.22in] flex items-center gap-2">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt="Logo"
            className="h-[0.42in] w-auto object-contain"
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
        ) : null}
        <div className="flex flex-col items-end leading-none">
          <div className="font-display font-black text-[0.19in] tracking-tighter">
            Decline Drive
          </div>
          <div className="font-mono-label text-[0.09in] text-[#d4a640] tracking-[0.2em] mt-1">
            SERVICE · PARTS · BODY SHOP
          </div>
        </div>
      </div>

      {/* Name block */}
      <div className="absolute left-[0.42in] bottom-[0.22in] flex items-end gap-[0.14in]">
        {fields.photo && (
          <img
            src={fields.photo}
            alt={fields.name}
            className="w-[0.72in] h-[0.72in] rounded-full object-cover border-2 border-[#d4a640] shrink-0"
            data-testid="biz-card-front-photo"
          />
        )}
        <div>
          <div
            className="font-display font-black text-[0.28in] tracking-tighter leading-none"
            data-testid="biz-card-front-name"
          >
            {fields.name}
          </div>
          <div className="font-display text-[0.14in] mt-1 text-[#d4a640]">
            {fields.title}
          </div>
        </div>
      </div>

      {/* Tagline bottom-right */}
      <div className="absolute right-[0.22in] bottom-[0.16in]">
        <div className="font-mono-label text-[0.09in] text-white/60 tracking-[0.25em] text-right">
          {fields.tagline}
        </div>
      </div>
    </div>
  );
}

function BusinessCardBack({ fields, logoUrl }) {
  return (
    <div
      className="biz-card relative bg-white text-neutral-900 overflow-hidden shadow-xl rounded-md border border-neutral-200"
      style={{ width: "3.5in", height: "2in" }}
      data-testid="biz-card-back"
    >
      {/* Gold accent stripe bottom */}
      <div
        className="absolute left-0 right-0 bottom-0 h-[0.12in] bg-[#d4a640]"
        aria-hidden
      />

      {/* Left: contact block */}
      <div className="absolute left-[0.22in] top-[0.22in] right-[1.2in]">
        <div className="flex items-center gap-2 mb-2">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Logo"
              className="h-[0.28in] w-auto object-contain"
              onError={(e) => (e.currentTarget.style.display = "none")}
            />
          ) : null}
          <div className="font-display font-black text-[0.15in] tracking-tighter text-[#0f2b6b]">
            Decline Drive
          </div>
        </div>

        <div className="space-y-[0.06in] mt-[0.14in]">
          <div className="flex items-baseline gap-2" data-testid="biz-card-back-phone">
            <div className="font-mono-label text-[0.075in] text-neutral-500 w-[0.4in] tracking-widest">
              CELL
            </div>
            <div className="font-display font-bold text-[0.11in]">{fields.phone}</div>
          </div>
          <div className="flex items-baseline gap-2" data-testid="biz-card-back-email">
            <div className="font-mono-label text-[0.075in] text-neutral-500 w-[0.4in] tracking-widest">
              EMAIL
            </div>
            <div className="font-display font-bold text-[0.11in] break-all">
              {fields.email}
            </div>
          </div>
          <div className="flex items-baseline gap-2" data-testid="biz-card-back-web">
            <div className="font-mono-label text-[0.075in] text-neutral-500 w-[0.4in] tracking-widest">
              WEB
            </div>
            <div className="font-display font-bold text-[0.11in]">{fields.website}</div>
          </div>
        </div>
      </div>

      {/* Right: QR code */}
      <div className="absolute right-[0.22in] top-[0.22in] flex flex-col items-center">
        <div className="p-1 bg-white rounded-sm border border-neutral-200">
          <DemoQRCode size={82} caption="" />
        </div>
        <div className="font-mono-label text-[0.065in] text-neutral-600 tracking-widest mt-1 text-center leading-tight">
          SCAN
          <br />
          SEE THE PITCH
        </div>
      </div>
    </div>
  );
}
